import { Check, X } from "lucide-react";
import { COMPARISON_ROWS } from "@/constants/content";

const Bad = ({ text, tone = "rose" }) => (
  <span className="flex items-start gap-2 text-sm text-slate-400">
    <X className={`mt-0.5 h-4 w-4 shrink-0 ${tone === "amber" ? "text-amber-400" : "text-rose-400"}`} aria-label="Weak" />
    <span>{text}</span>
  </span>
);
const Good = ({ text }) => (
  <span className="flex items-start gap-2 text-sm font-medium text-white">
    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-label="Strong" />
    <span>{text}</span>
  </span>
);
const ColHead = ({ name, sub, highlight }) => (
  <div className={`p-4 ${highlight ? "iv-win-col rounded-t-2xl border-t" : ""}`}>
    <div className={`text-sm font-bold ${highlight ? "text-emerald-300" : "text-white"}`}>{name}</div>
    <div className="mt-1">
      {highlight ? (
        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">Strong / Ideal Fit</span>
      ) : (
        <span className="text-[11px] text-slate-500">{sub}</span>
      )}
    </div>
  </div>
);

const DesktopTable = () => (
  <div className="hidden md:grid md:grid-cols-[1.1fr_1fr_1fr_1.25fr] md:gap-x-3" role="table" aria-label="Feature comparison" data-testid="comparison-table-desktop">
    <div className="p-4 text-[11px] uppercase tracking-wider text-slate-500" role="columnheader">Parameter</div>
    <ColHead name="ValidatorAI" sub="Attachment benchmark" />
    <ColHead name="VenturusAI / DimeADozen" sub="Generic AI idea tools" />
    <ColHead name="IdeaValidator360" highlight />
    {COMPARISON_ROWS.map((r, i) => {
      const last = i === COMPARISON_ROWS.length - 1;
      return [
        <div key={`${r.key}-l`} className="border-t border-white/[0.06] p-4 text-sm font-semibold text-slate-200" role="rowheader">{r.label}</div>,
        <div key={`${r.key}-v`} className="border-t border-white/[0.06] p-4"><Bad text={r.validatorai} /></div>,
        <div key={`${r.key}-g`} className="border-t border-white/[0.06] p-4"><Bad text={r.generic} tone="amber" /></div>,
        <div key={`${r.key}-i`} className={`iv-win-col p-4 ${last ? "rounded-b-2xl border-b" : ""}`} data-testid={`comparison-iv360-${r.key}`}><Good text={r.iv360} /></div>,
      ];
    })}
  </div>
);

const MobileCards = () => (
  <div className="space-y-3 md:hidden" data-testid="comparison-cards-mobile">
    {COMPARISON_ROWS.map((r) => (
      <article key={r.key} className="iv-glass rounded-2xl p-4" data-testid={`comparison-card-${r.key}`}>
        <h3 className="text-sm font-bold text-white">{r.label}</h3>
        <div className="mt-3 rounded-xl border border-emerald-400/40 bg-emerald-500/[0.06] p-3 shadow-[0_0_20px_rgba(16,185,129,0.12)]">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-emerald-300">IdeaValidator360</div>
          <Good text={r.iv360} />
        </div>
        <div className="mt-2 grid gap-2">
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">ValidatorAI</div><Bad text={r.validatorai} /></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500">VenturusAI / DimeADozen</div><Bad text={r.generic} tone="amber" /></div>
        </div>
      </article>
    ))}
  </div>
);

export const ComparisonMatrix = () => (
  <section className="py-12" aria-labelledby="compare-title" data-testid="comparison-section">
    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300">Competitive Benchmark Matrix</p>
    <h2 id="compare-title" className="mt-2 max-w-3xl text-base font-bold text-white md:text-lg">
      Why Founders Switch From ValidatorAI & Generic Tools to IdeaValidator360
    </h2>
    <p className="mt-2 max-w-2xl text-sm text-slate-400">Benchmarked on the HSR LunchDrop reference case (76.8 / 100).</p>
    <div className="iv-glass mt-6 rounded-2xl p-2 md:p-3">
      <DesktopTable />
      <MobileCards />
    </div>
    <p className="mt-3 text-[11px] text-slate-500">Comparison reflects publicly visible sample outputs reviewed by IdeaValidator360; competitor features may change.</p>
  </section>
);
